import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { Collections, SearchCollections } from '../collections.model';
import { CollectionActions } from './actions';
import { StoreBackend } from './backend';
import {
  arrayIsShallowEqual,
  isDefined,
  objectFilterByKeys,
  sameElements,
  setDiff,
  toNumber,
  toObservable,
} from './helpers';
import { ListObservable } from './list-observable';
import { ObjectObservable } from './object-observable';
import { duplicateFirst } from './operators/duplicateFirst';
import { select } from './operators/select';
import { Query } from './query';
import { SearchManager } from './search';
import { stringifyQuery } from './store.helpers';
import {
  Path,
  QueryParams,
  Reference,
  SearchQuery,
  StoreState,
} from './store.model';
import { WebSocketService } from './websocket';

@Injectable()
export class Datastore {
  constructor(
    private store: Store<StoreState>,
    private storeBackend: StoreBackend,
    private webSocketService: WebSocketService,
    private searchManager: SearchManager,
  ) {}

  list<K extends keyof Collections>(
    key1: K,
    key2: string | Observable<string>,
    queryFn?: (q: Query<K>) => Query<K>,
  ): ListObservable<K> {
    const rawQuery = queryFn ? queryFn(new Query()).query : {};

    const refStream: Observable<Reference<K>> = Observable.combineLatest(
      [
        toObservable(key1),
        toObservable(key2),
        toObservable(rawQuery.limit),
        toObservable(rawQuery.offset),
        toObservable(rawQuery.params),
      ],
      (
        collection: K,
        authUid: string,
        limit: number,
        offset: number,
        params: QueryParams<Collections[K]>,
      ) => {
        const path: Path<K> = { collection, authUid };
        return { path, query: { limit, offset, params } };
      },
    ).pipe(
      tap(ref => {
        const action: CollectionActions<K> = {
          type: 'REQUEST_DATA',
          payload: {
            type: ref.path.collection,
            ref,
          },
        };
        this.store.dispatch(action);
      }),
      shareReplay(1),
    );

    return new ListObservable(
      refStream,
      this.storeBackend,
      refStream.pipe(
        switchMap(({ path: { collection, authUid }, query }) =>
          this.store.pipe(
            tap(source => {
              if (!(collection in source)) {
                throw new Error(
                  `Missing collection "${collection}", please import the corresponding feature module`,
                );
              }
            }),
            select(collection, authUid),
            map(data => {
              const queryString = stringifyQuery<K>(query);
              if (data && data.lists && data.lists[queryString]) {
                return ((data.lists[queryString] || { ids: [] }).ids || [])
                  .slice((query && query.offset) || 0)
                  .map(id => data.entities[id].entity);
              }
              return undefined;
            }),
            filter(isDefined),
            distinctUntilChanged(arrayIsShallowEqual),
          ),
        ),
        shareReplay(1),
      ),
    );
  }

  /* Returns an observable with a single object.
   */
  object<K extends keyof Collections>(
    key1: K,
    key2: string | Observable<string>,
    objectId: string | number | Observable<string> | Observable<number>,
  ): ObjectObservable<K> {
    const refStream = Observable.combineLatest(
      [
        toObservable(key1),
        toObservable(key2),
        toObservable<number | string>(objectId).pipe(distinctUntilChanged()),
      ],
      (collection: K, authUid: string, id: string | number) => {
        const path: { collection: K; authUid: string; ids: string[] } = {
          collection,
          authUid,
          ids: [id.toString()],
        };
        return { path };
      },
    ).pipe(
      switchMap(
        ({ path: { collection, authUid } }) =>
          this.store.pipe(
            tap(source => {
              if (!(collection in source)) {
                throw new Error(
                  `Missing collection "${collection}", please import the corresponding feature module`,
                );
              }
            }),
            select(collection, authUid),
            take(1),
          ),
        ({ path }, data) => ({ ref: { path }, data }),
      ),
      tap(({ ref, data }) => {
        const { path: { ids } } = ref;
        // Don't fetch if this was fetched in the last second.
        if (
          !data ||
          !data.entities[ids[0]] ||
          toNumber(data.entities[ids[0]].timeFetched) + 10000 < Date.now()
        ) {
          const action: CollectionActions<K> = {
            type: 'REQUEST_DATA',
            payload: {
              type: ref.path.collection,
              ref,
            },
          };
          this.store.dispatch(action);
        }
      }),
      map(({ ref }) => ref),
      shareReplay(1),
    );

    const subscriptionStream =
      key1 === 'onlineOffline'
        ? this.webSocketService
            .subscribeOnlineOffline(
              refStream.pipe(
                map(({ path: { collection, authUid, ids } }) => ids),
              ),
            )
            .pipe(combineLatest(refStream, (ss, ref) => ref))
        : refStream;

    return new ObjectObservable(
      Observable.merge(refStream, subscriptionStream).pipe(
        switchMap(({ path: { collection, authUid, ids } }) =>
          this.store.pipe(
            select(collection, authUid),
            filter(isDefined),
            map(obj => obj.entities[ids[0]] && obj.entities[ids[0]].entity),
            filter(isDefined),
            distinctUntilChanged(),
          ),
        ),
        shareReplay(1),
      ),
    );
  }

  /* Returns a list of objects at a location.
   * If an observable is passed in for key2 then only the delta will be
   * refetched from the network.
   */
  objects<K extends keyof Collections>(
    key1: K,
    key2: string | Observable<string>,
    objectIds:
      | ReadonlyArray<number>
      | ReadonlyArray<string>
      | Observable<ReadonlyArray<number>>
      | Observable<ReadonlyArray<string>>,
  ): Observable<ReadonlyArray<Collections[K]>> {
    const refStream = Observable.combineLatest(
      [
        toObservable(key1),
        toObservable(key2),
        toObservable<ReadonlyArray<number> | ReadonlyArray<string>>(
          objectIds,
        ).pipe(distinctUntilChanged(arrayIsShallowEqual)),
      ],
      (
        collection: K,
        authUid: string,
        ids: ReadonlyArray<number | string>, // This is a bug in TS
      ) => {
        const path: { collection: K; authUid: string; ids: string[] } = {
          collection,
          authUid,
          ids: ids.map(id => id.toString()),
        };
        return { path };
      },
    ).pipe(
      // Add an path at the start with an empty list of ids
      duplicateFirst<{
        path: { collection: K; authUid: string; ids: string[] };
      }>(({ path: { collection, authUid } }) => ({
        path: { collection, authUid, ids: [] },
      })),
      pairwise(), // Use pairwise and only fetch new things
      switchMap(
        ([{ path: { collection, authUid } }]) =>
          this.store.pipe(select(collection, authUid), take(1)),
        (paths, data) => ({ paths, data }),
      ),
      tap(
        ({
          paths: [
            { path: { ids: idsOrig } },
            { path: { collection, authUid, ids: idsNew } },
          ],
          data,
        }) => {
          const newIds = setDiff(idsNew, idsOrig).filter(
            id =>
              data && data.entities[id]
                ? toNumber(data.entities[id].timeFetched) + 10000 < Date.now()
                : true,
          );
          if (newIds.length) {
            const action: CollectionActions<K> = {
              type: 'REQUEST_DATA',
              payload: {
                type: collection,
                ref: { path: { collection, authUid, ids: newIds } },
              },
            };
            this.store.dispatch(action);
          }
        },
      ),
      map(({ paths: [{ path: collection }, { path: authUid }] }) => ({
        path: authUid,
      })),
      shareReplay(1),
    );

    const subscriptionStream =
      key1 === 'onlineOffline'
        ? this.webSocketService
            .subscribeOnlineOffline(
              refStream.pipe(
                map(({ path: { collection, authUid, ids } }) => ids),
              ),
            )
            .pipe(combineLatest(refStream, (ss, ref) => ref))
        : refStream;

    return Observable.merge(refStream, subscriptionStream).pipe(
      switchMap(({ path: { collection, authUid, ids } }) =>
        this.store.pipe(
          tap(source => {
            if (!(collection in source)) {
              throw new Error(
                `Missing collection "${collection}", please import the corresponding feature module`,
              );
            }
          }),
          select(collection, authUid),
          filter(isDefined),
          map(entitiesObject =>
            objectFilterByKeys<StoreState[K][string]['entities'][string]>(
              entitiesObject.entities,
              ids,
            ),
          ),
          map(entitiesObject => Object.values(entitiesObject)),
          map(entities => entities.map(entity => entity.entity)),
          distinctUntilChanged(sameElements),
        ),
      ),
      shareReplay(1),
    );
  }

  // Hits an elastic search endpoint directly and returns the result.
  // The data doesn't get stored in the datastore (at least yet).
  search<K extends keyof SearchCollections>(
    key: K,
    query: SearchQuery | Observable<SearchQuery>,
  ): Observable<SearchCollections[K]> {
    return this.storeBackend
      .search(key, toObservable(query))
      .pipe(map(result => this.searchManager.transform(key, result)));
  }

  /* Creates a single object.
  */
  createObject<K extends keyof Collections>(
    key1: K,
    key2: string | Observable<string>,
    object: Collections[K],
    extra?: { [index: string]: string | number },
  ): Promise<boolean> {
    return Observable.combineLatest(
      [toObservable(key1), toObservable(key2)],
      (path1: K, path2: string) => {
        const path: Path<K> = {
          collection: path1,
          authUid: path2,
        };
        return { path };
      },
    )
      .pipe(
        take(1),
        switchMap((ref: Reference<K>) =>
          this.storeBackend.push(ref, object, extra),
        ),
      )
      .toPromise();
  }
}



// WEBPACK FOOTER //
// ./src/@freelancer/datastore/core/datastore.ts